// src/components/solutions/SolutionsProcess.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';

interface Step {
  step: string;
  icon: string;
  title: string;
  text: string;
}

interface ProcessData {
  headline: string;
  steps: Step[];
}

interface SolutionsProcessProps {
  processData: ProcessData;
}

export const SolutionsProcess: React.FC<SolutionsProcessProps> = ({ processData }) => {
  // --- FIX: Add a defensive guard clause ---
  if (!processData || !Array.isArray(processData.steps)) {
    return null;
  }
  // --- END OF FIX ---
  
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-16">{processData.headline}</h2>
        
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-slate-200 hidden md:block"></div>
          
          <div className="space-y-12 md:space-y-0">
            {processData.steps.map((item, index) => {
              const isEven = index % 2 === 0;
              const cardVariants = {
                hidden: { opacity: 0, x: isEven ? -50 : 50 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } }
              };
              return (
                <div key={index} className="md:grid md:grid-cols-2 md:gap-x-8 items-center">
                  {/* Card Content (Order changes based on position) */}
                  <motion.div
                    className={clsx("md:col-start-1 md:row-start-1", isEven ? "md:col-start-1" : "md:col-start-2")}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <div className="bg-slate-50 p-6 rounded-xl border-2 border-black relative">
                      {/* Triangle pointer */}
                      <div className={clsx(
                        "hidden md:block absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-slate-50 border-black",
                         isEven ? "right-[-9px] border-t-2 border-r-2 rotate-45" : "left-[-9px] border-b-2 border-l-2 rotate-45"
                      )}></div>
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-white border-2 border-black rounded-lg">
                          {renderIcon(item.icon, { size: 24, className: 'text-gray-800' })}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{item.title}</h3>
                      </div>
                      <p className="mt-3 text-slate-600">{item.text}</p>
                    </div>
                  </motion.div>
                  
                  {/* Step Number Circle */}
                  <div className={clsx("hidden md:flex justify-center items-center md:row-start-1", isEven ? "md:col-start-2" : "md:col-start-1")}>
                     <div className="bg-emerald-500 z-10 w-12 h-12 flex items-center justify-center rounded-full border-2 border-black text-white font-bold text-lg">
                        {item.step}
                     </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

